import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import type { ComponentType } from 'react';

import { MissionKindEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import {
  PATH_FOLDER_DEFAULT,
  PATH_FOLDER_MISSIONS,
  PATH_FOLDERS_DEFAULT,
  PATH_MISSION_DEFAULT,
  PATH_MISSION_SUB_MISSIONS,
} from '../../app/constants/paths.constant';
import { MissionSpaceEnum } from '../../app/enums/mission-space.enum';
import { MISSION_SPACE_PARAM } from '../../app/utils/mission-space.util';
import { missionKindProfileOf } from '../../app/utils/mission-kind-profile.util';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SimpleMissionCreationForm } from '../../forms/SimpleMission';
import { SubMissionCreationForm } from '../../forms/SubMission';
import { CollaborationCreationForm } from '../../forms/Collaboration';
import type { MissionCreationFormProps } from '../../forms/Mission/types/mission-creation-form-props.type';
import { Loading } from '../../components/Loading';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useFolderContext } from '../../contexts/FolderContext/hooks/useFolderContext.hook';
import { useMissionContextOptional } from '../../contexts/MissionContext/hooks/useMissionContextOptional.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

// Each kind of mission is created with its own form.
const CREATION_FORMS: Readonly<
  Record<MissionKindEnum, ComponentType<MissionCreationFormProps>>
> = {
  [MissionKindEnum.Standard]: SimpleMissionCreationForm,
  [MissionKindEnum.Composite]: CollaborationCreationForm,
  [MissionKindEnum.SubMission]: SubMissionCreationForm,
};

interface Props {
  // A collaboration (/collaborations/nouvelle), a mission attached to the
  // collaboration of the route (/collaborations/:missionId/sous-missions/nouvelle),
  // or a simple mission.
  readonly kind?: MissionKindEnum;
}

export const NewMissionPage = ({
  kind = MissionKindEnum.Standard,
}: Readonly<Props>) => {
  const { folderId, folder } = useFolderContext();

  // Only the creation of a mission attached to a collaboration has one.
  const { missionId: collaborationId } = useParams();
  const { creationPath, folderListCrumb, isWithinACollaboration, wording } =
    missionKindProfileOf(kind);
  const compositeMissionId =
    isWithinACollaboration && collaborationId ? (collaborationId as Id) : null;
  const entityLabel = wording.name;
  const CreationForm = CREATION_FORMS[kind];

  // A sub-mission inherits its information and address from its
  // collaboration (the mission of the route), not from the folder.
  const collaboration = useMissionContextOptional()?.mission;
  const parentConsolidation = isWithinACollaboration
    ? collaboration?.consolidation
    : folder?.consolidation;

  const { paths } = useBuildPaths(
    {
      default: PATH_FOLDER_DEFAULT,
      missions: PATH_FOLDER_MISSIONS,
      newMission: creationPath,
      collaboration: PATH_MISSION_DEFAULT,
      subMissions: PATH_MISSION_SUB_MISSIONS,
    },
    {
      folderId,
      missionId: collaborationId,
      [MISSION_SPACE_PARAM]: MissionSpaceEnum.Collaborations,
    },
  );

  const collaborationPaths = compositeMissionId
    ? [
        {
          to: paths.collaboration,
          label: 'collaboration',
          key: 'collaboration',
        },
        {
          to: paths.subMissions,
          label: 'sous-missions',
          key: 'subMissions',
        },
      ]
    : [];

  return (
    <>
      <Helmet>
        <title>{buildDocumentTitle(`Création d'une ${entityLabel}`)}</title>
      </Helmet>
      <Breadcrumbs
        paths={[
          {
            to: PATH_FOLDERS_DEFAULT,
            label: 'dossiers',
            key: 'folders',
          },
          {
            to: paths.default,
            label: 'dossier',
            key: 'folder',
          },
          {
            to: paths.missions,
            label: folderListCrumb,
            key: 'missions',
          },
          ...collaborationPaths,
          {
            to: paths.newMission,
            label: 'nouvelle',
            key: 'newMission',
          },
        ]}
      />
      <PageTitle title={`Création d'une ${entityLabel}`} />
      {!parentConsolidation ? (
        <Loading />
      ) : (
        <CreationForm
          compositeMissionId={compositeMissionId}
          folderConsolidation={parentConsolidation}
        />
      )}
    </>
  );
};
